import React, { useMemo } from 'react';
import Dropdown, { DropdownTheme } from 'Douleutaras/components/Dropdown/Dropdown';
import DropdownItem from 'Douleutaras/components/Dropdown/DropdownItem';
import { getAbsoluteUrl } from 'Douleutaras/utilities/url-builder';
import { CategoryMenu } from 'Douleutaras/types';
import { URLS } from 'Douleutaras/constants/urls';
import { Trans, useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import DefaultHeaderUserMenu from 'Douleutaras/layout/partials/DefaultHeaderUserMenu/DefaultHeaderUserMenu';
import CountBadge from 'Douleutaras/components/Badge/CountBadge';
import { CategoriesMenuItem } from 'Douleutaras/types/categoriesMenu';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { notificationEndpoint } from 'Douleutaras/redux/data/notifications/notificationsSlice';
import { selectIsCreditUser } from 'Douleutaras/redux/data/auth/authSlice';
import { useSubscriptionTier } from 'Douleutaras/hooks/users/proSubscriptionTier';

import MenuChild from './MenuChild';
import './DefaultHeaderMobile.scss';

interface DefaultHeaderMobileProps {
  activeJobsCount: number; // number of unread active jobs
  categoriesMenu: Array<CategoryMenu>;
  isPro: boolean;
  unreadMessagesCount: number;
  userDropdown: boolean;
  openCallback(dropdownStatus: boolean): void;
  setUserDropdown(open: boolean): void;
}

export const DefaultHeaderMobile = ({
                                      activeJobsCount,
                                      openCallback,
                                      unreadMessagesCount,
                                      userDropdown,
                                      isPro,
                                      setUserDropdown,
                                      categoriesMenu
                                    }: DefaultHeaderMobileProps) => {
  const {t} = useTranslation();
  const settingsUrl = isPro ? getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.GENERAL') : getAbsoluteUrl('CLIENT_DASHBOARD.SETTINGS');
  const isSettingsUrl = Boolean(useMatch(settingsUrl));
  const aggregatedCount = unreadMessagesCount + activeJobsCount;
  // @ts-ignore
  const notificationsMeta = useAppSelector(state => state.notifications.meta?.[notificationEndpoint]?.meta);
  const isCreditUser = useAppSelector(selectIsCreditUser);
  const { isGoldOrSilverTier, currentSubscriptionTier } = useSubscriptionTier();
  const isStatsCenterActive = Boolean(useMatch({
    path: getAbsoluteUrl('PRO_DASHBOARD.STATS_CENTER.INDEX'),
    end: false
  }));
  const isNotificationsCenterActive = Boolean(useMatch({
    path: getAbsoluteUrl('PRO_DASHBOARD.NOTIFICATIONS_CENTER'),
    end: false
  }));

  const menuItems = [
    { text: t('My jobs'), url: URLS.SELLER.MY_JOBS, dataTracking: 'pro-dashboard-navbar__assigned-jobs', isReactRoute: true },
    { text: t('Concluded'), url: URLS.SELLER.CONCLUDED, dataTracking: 'pro-dashboard-navbar__completed-jobs', isReactRoute: true }
  ];

  const menuItemsProV2 = [
    { text: t('T.PRO-HEADER.MY JOBS'), url: URLS.SELLER.MY_JOBS, dataTracking: 'pro-dashboard-navbar__assigned-jobs',
      isActive: Boolean(useMatch(URLS.SELLER.MY_JOBS)), isReactRoute: true
    },
    { text: t('T.PRO-HEADER.CONCLUDED JOBS'), url: URLS.SELLER.CONCLUDED_v2, dataTracking: 'pro-dashboard-navbar__completed-jobs',
      isActive: Boolean(useMatch(URLS.SELLER.CONCLUDED_v2)), isReactRoute: true
    },
    { text: t('T.PRO-HEADER.CANCELLED JOBS'), url: URLS.SELLER.CANCELLED, dataTracking: 'pro-dashboard-navbar__cancelled-jobs',
      isActive: Boolean(useMatch(URLS.SELLER.CANCELLED)), isReactRoute: true
    }
  ];

  if (isGoldOrSilverTier) {
    menuItems.push({
      text: t('Proposals'),
      url: `${URLS.SELLER.PROPOSED}?tier=${currentSubscriptionTier}`,
      dataTracking: 'pro-dashboard-navbar__available-jobs',
      isReactRoute: false
    });
    menuItemsProV2.push(
      {
        text: t('Proposals'),
        url: `${URLS.SELLER.PROPOSED}?tier=${currentSubscriptionTier}`,
        dataTracking: 'pro-dashboard-navbar__available-jobs',
        isReactRoute: false,
        isActive: false
      }
    );
  }

  const activeMenuItemPro = menuItemsProV2.find(menuItem => menuItem.isActive);

  // Memoized so toggling the dropdown open/closed doesn't rebuild and
  // re-reconcile this whole (already-mounted) list on every click.
  const categoriesItems = useMemo(() => (
    categoriesMenu?.map((item: CategoriesMenuItem, i) => {

      return (
        <div styleName="dropdown-item" key={`${item.url}-${i}`}>
          <MenuChild
            id={`subcat-${i}`}
            title={item.title}
            icon={<img src={item.icon} alt="" />}
            closeCallback={setUserDropdown}
          >
            {item.children?.map(finalCategory => {
              return (
                <div styleName="dropdown-item" key={finalCategory.url}>
                  <DropdownItem
                    text={finalCategory.title}
                    style="divided"
                    href={finalCategory.url}
                  />
                </div>
              );
            })}
          </MenuChild>
        </div>
      );
    })
  ), [categoriesMenu, setUserDropdown]);

  return (
    <Dropdown
      showHeader
      callback={openCallback}
      isOpen={userDropdown}
      theme={DropdownTheme.REGULAR}
      header={
        <header styleName="user-dropdown__header">
          {userDropdown
            ? <button type="button" styleName="user-avatar-wrapper">
              {!isPro &&
                <div styleName="control-caption">
                  <Trans>Close Menu</Trans>
                </div>
              }
              <div styleName="control-icon control-icon--close">
                <div className="dashicon dashicon-close"/>
              </div>
            </button>
            : <button type="button" styleName="user-avatar-wrapper">
              {!isPro && <div styleName="control-caption"><Trans>Menu</Trans></div>}
              <div styleName="control-icon control-icon--open">
                <div className="dashicon dashicon-burger-menu"/>
                {!isPro && aggregatedCount > 0 &&
                  <CountBadge count={aggregatedCount}/>
                }
              </div>
            </button>
          }
        </header>
      }
      footer={
        <DefaultHeaderUserMenu setUserDropdown={setUserDropdown} isPro={isPro} isSettingsUrl={isSettingsUrl}/>
      }
    >
      <div styleName="dropdown-item dropdown-item--highlighted">
        {!isPro
        ? <MenuChild id="categories"
                   title={t('All Categories')}
                   icon={<span className="dashicon dashicon-briefcase"/>}
                   highlight
                   closeCallback={setUserDropdown}
          >
            {categoriesItems}
          </MenuChild>
        : <MenuChild id="jobs"
                     title={activeMenuItemPro ? activeMenuItemPro.text : t('JOBS')}
                     icon={<span className="dashicon dashicon-tools"/>}
                     highlight
                     closeCallback={setUserDropdown}
          >
            { isCreditUser
              ? menuItems.map((item, i) => (
                <div styleName="dropdown-item-underline" key={`${item.url}-${i}`}>
                  <DropdownItem
                    key={i}
                    text={item.text}
                    href={item.url}
                    dataTracking={item.dataTracking}
                    isReactRoute={item.isReactRoute}
                    onClick={() => setUserDropdown(false)}
                  />
                </div>
              ))
              : menuItemsProV2.map((item, i) => (
                <div styleName="dropdown-item-underline" key={`${item.url}-${i}`}>
                  <DropdownItem
                    active={item.isActive}
                    key={i}
                    text={item.text}
                    href={item.url}
                    dataTracking={item.dataTracking}
                    isReactRoute={item.isReactRoute}
                    onClick={() => setUserDropdown(false)}
                  />
                </div>
              ))}
          </MenuChild>
        }
      </div>
      {isPro &&
        <div styleName="dropdown-item">
          <DropdownItem
            icon={<span className="dashicon dashicon-statcenter"/>}
            text={t('STATS')}
            isReactRoute
            style="main"
            href={getAbsoluteUrl('PRO_DASHBOARD.STATS_CENTER.INDEX')}
            dataTracking="pro-dashboard-navbar__stats"
            active={isStatsCenterActive}
            onClick={() => setUserDropdown(false)}
          />
        </div>
      }
      <div styleName="dropdown-item">
        <DropdownItem
          icon={<div styleName="dropdown-item-icon">
            <span className="dashicon dashicon-chat-bubble-outline"/>
            {unreadMessagesCount > 0 && <CountBadge count={unreadMessagesCount}/>}
          </div>}
          text={t('T.HEADER.MY-MESSAGES')}
          style="main"
          href={isPro
            ? getAbsoluteUrl('PRO_DASHBOARD.CONVERSATION.INDEX')
            : getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.INDEX')
          }
        />
      </div>
      {!isPro &&
        <div styleName="dropdown-item">
          <DropdownItem
            icon={<div styleName="dropdown-item-icon">
              <span className="dashicon dashicon-task"/>
              {activeJobsCount > 0 && <CountBadge count={activeJobsCount}/>}
            </div>}
            text={t('My jobs')}
            style="main"
            href={getAbsoluteUrl('CLIENT_DASHBOARD.INDEX')}
          />
        </div>
      }
      {isPro &&
        <div styleName="dropdown-item">
          <DropdownItem
            icon={<div styleName="dropdown-item-icon">
              <span className="dashicon dashicon-bell"/>
              {notificationsMeta && notificationsMeta.unreadCount > 0 && <CountBadge count={notificationsMeta.unreadCount}/>}
            </div>}
            text={t('T.NC Notifications')}
            isReactRoute
            style="main"
            href={getAbsoluteUrl('PRO_DASHBOARD.NOTIFICATIONS_CENTER')}
            dataTracking="pro-dashboard-navbar__notification"
            active={isNotificationsCenterActive}
            onClick={() => setUserDropdown(false)}
          />
        </div>
      }
    </Dropdown>
  );
};

export default DefaultHeaderMobile;
